import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

import "../../../styles/file-record-card-skeleton.css";

function formatLabel(label: string | undefined): string {
  const raw = label?.trim() || "Working";
  const capitalized = raw.charAt(0).toUpperCase() + raw.slice(1);
  if (/[.!?…]\s*$/.test(capitalized)) return capitalized;
  return capitalized + "...";
}

/** Static placeholder shapes — the highlight comes from the card's band. */
const SKELETON_BAR_CLASS = "rounded-sm bg-muted-foreground/15";

export function SkeletonContentLines() {
  const lineWidths = ["100%", "92%", "85%", "96%", "78%"];
  return (
    <div className="w-full h-full px-4 py-3.5 flex flex-col gap-2 justify-center">
      <div className={cn("h-2.5 w-3/5 mb-1", SKELETON_BAR_CLASS)} />
      {lineWidths.map((w, i) => (
        <div
          key={i}
          className={cn("h-2", SKELETON_BAR_CLASS)}
          style={{ width: w }}
        />
      ))}
    </div>
  );
}

export type FileRecordSkeletonKind = "image" | "video" | "file_record";

function resolveDefaultHeight(
  width: number,
  kind: FileRecordSkeletonKind | undefined,
): number {
  switch (kind) {
    case "image":
      return Math.round(width * (768 / 1408));
    case "video":
      return Math.round(width * (9 / 16));
    case "file_record":
    default:
      // The portrait page box (320×416, Letter on the 16px lattice — the
      // canvas's DOC_PORTRAIT_* default for every non-media file record) so
      // the node doesn't jump height when the skeleton swaps to the card.
      return Math.round(width * (26 / 20));
  }
}

export interface FileRecordCardSkeletonProps {
  width: number;
  height?: number;
  kind?: FileRecordSkeletonKind;
  label?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Shimmer loading skeleton for file record canvas nodes. Mirrors the
 * embedded `FileRecordCard` layout exactly so the skeleton → real card
 * swap has zero visible reflow. Used for all loading states: agent
 * placement, file upload in-flight, workspace copy, and backend
 * processing (pending/processing status with no URL yet).
 *
 * The shapes and label are static; a single highlight band
 * (`.file-record-skeleton__band`, transform-animated and clipped by the
 * card) sweeps across the whole card — one animation per skeleton.
 */
export function FileRecordCardSkeleton({
  width,
  height,
  kind,
  label,
  children,
  className,
}: FileRecordCardSkeletonProps) {
  const resolvedHeight = height ?? resolveDefaultHeight(width, kind);
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label || "Loading"}
      className={cn(
        "relative flex flex-col overflow-hidden rounded-xl bg-card opacity-75",
        className,
      )}
      style={{ width, height: resolvedHeight, containerType: "inline-size" }}
    >
      <div className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden">
        {children ?? <SkeletonContentLines />}
      </div>
      <div className="flex items-center gap-2 shrink-0 px-2.5 py-2">
        <p className="flex-1 min-w-0 text-[12px] font-semibold leading-tight truncate text-muted-foreground">
          {formatLabel(label)}
        </p>
      </div>
      <div className="file-record-skeleton__band" aria-hidden />
    </div>
  );
}

export default FileRecordCardSkeleton;
